import { jsx as _jsx } from "react/jsx-runtime";
import { getLoggedInCustomer } from '@/api/customer.api';
import { getRecruiterOrganization } from '@/api/organization.api';
import { useQuery } from '@/hooks/use-query';
import { getToken, getTokenDetails } from '@/utils';
import { createContext, useContext, useEffect, useState } from 'react';
import { useConfigContext } from './config-context';
var CustomerDetailContext = createContext(null);
export var CustomerDetailProvider = function (_a) {
    var children = _a.children, _b = _a.isPrivate, isPrivate = _b === void 0 ? false : _b;
    var _c = useState(null), recruiterToken = _c[0], setRecruiterToken = _c[1];
    var _d = useConfigContext().config, tokenKey = _d.tokenKey, recruiterTokenKey = _d.recruiterTokenKey, shouldIncludeAdminModule = _d.shouldIncludeAdminModule, jobListingPageUrl = _d.jobListingPageUrl;
    var _e = useState(function () { return getToken(tokenKey); }), token = _e[0], setToken = _e[1];
    var _f = useQuery({
        queryFn: getLoggedInCustomer,
        config: {
            enabled: !!token,
        },
    }), customerDetail = _f.data, refetchCustomerDetail = _f.refetch, setCustomerDetail = _f.setData, isLoadingCustomerDetail = _f.isLoading;
    var _g = useQuery({
        queryFn: getRecruiterOrganization,
        config: {
            enabled: shouldIncludeAdminModule && !!recruiterToken,
        },
    }), recruiterOrganization = _g.data, refetchRecruiterOrganization = _g.refetch, setRecruiterOrganization = _g.setData;
    useEffect(function () {
        if (tokenKey) {
            setToken(getToken(tokenKey));
        }
        if (recruiterTokenKey) {
            setRecruiterToken(getToken(recruiterTokenKey));
        }
    }, [tokenKey, recruiterTokenKey]);
    useEffect(function () {
        if (shouldIncludeAdminModule && recruiterToken) {
            refetchRecruiterOrganization();
        }
        else {
            setRecruiterOrganization(null);
        }
    }, [
        recruiterToken,
        shouldIncludeAdminModule,
        refetchRecruiterOrganization,
        setRecruiterOrganization,
    ]);
    useEffect(function () {
        if (token) {
            refetchCustomerDetail();
        }
        else {
            setCustomerDetail(null);
        }
    }, [token, refetchCustomerDetail, setCustomerDetail]);
    useEffect(function () {
        var tokenDetail = tokenKey ? getTokenDetails(tokenKey) : null;
        var recruiterTokenDetail = recruiterTokenKey ? getTokenDetails(recruiterTokenKey) : null;
        // Function to handle localStorage changes from other tabs/windows
        var handleStorageChange = function (event) {
            if ((tokenDetail === null || tokenDetail === void 0 ? void 0 : tokenDetail.type) === 'localstorage' &&
                event.key === tokenDetail.key &&
                event.newValue !== event.oldValue) {
                if (event.newValue === null && isPrivate) {
                    setCustomerDetail(null);
                    window.location.href = jobListingPageUrl !== null && jobListingPageUrl !== void 0 ? jobListingPageUrl : '/';
                }
                setToken(event.newValue);
            }
            if ((recruiterTokenDetail === null || recruiterTokenDetail === void 0 ? void 0 : recruiterTokenDetail.type) === 'localstorage' &&
                event.key === recruiterTokenDetail.key &&
                event.newValue !== event.oldValue) {
                setRecruiterToken(event.newValue);
            }
        };
        // Custom function to monitor localStorage in the current tab
        var setupStorageMonitor = function () {
            // Store original setItem method
            var originalSetItem = localStorage.setItem;
            var originalRemoveItem = localStorage.removeItem;
            var originalClear = localStorage.clear;
            // Override setItem
            localStorage.setItem = function (key, value) {
                // Call original method
                originalSetItem.apply(this, [key, value]);
                // Create and dispatch event
                var storageEvent = new StorageEvent('storage', {
                    key: key,
                    newValue: value,
                    oldValue: localStorage.getItem(key),
                    storageArea: localStorage,
                });
                window.dispatchEvent(storageEvent);
            };
            // Override removeItem
            localStorage.removeItem = function (key) {
                var oldValue = localStorage.getItem(key);
                // Call original method
                originalRemoveItem.apply(this, [key]);
                // Create and dispatch event
                var storageEvent = new StorageEvent('storage', {
                    key: key,
                    newValue: null,
                    oldValue: oldValue,
                    storageArea: localStorage,
                });
                window.dispatchEvent(storageEvent);
            };
            // Override clear
            localStorage.clear = function () {
                // Call original method
                originalClear.apply(this);
                // Create and dispatch event
                var storageEvent = new StorageEvent('storage', {
                    storageArea: localStorage,
                });
                window.dispatchEvent(storageEvent);
            };
            return function () {
                // Restore original methods when cleaning up
                localStorage.setItem = originalSetItem;
                localStorage.removeItem = originalRemoveItem;
                localStorage.clear = originalClear;
            };
        };
        // Add event listener for storage events from other tabs
        if (tokenDetail || recruiterTokenDetail) {
            window.addEventListener('storage', handleStorageChange);
            // Set up monitoring for current tab localStorage changes
            var cleanupMonitor_1 = setupStorageMonitor();
            return function () {
                window.removeEventListener('storage', handleStorageChange);
                cleanupMonitor_1();
            };
        }
        return function () {
            window.removeEventListener('storage', handleStorageChange);
        };
    }, [tokenKey, recruiterTokenKey, isPrivate, jobListingPageUrl, setCustomerDetail]);
    return (_jsx(CustomerDetailContext.Provider, { value: { customerDetail: customerDetail, recruiterOrganization: recruiterOrganization }, children: children }));
};
export var useCustomerDetailContext = function () {
    var context = useContext(CustomerDetailContext);
    if (!context) {
        throw new Error('useCustomerDetailContext must be used within CustomerDetailContextProvider');
    }
    return context;
};
